import { useState, useEffect } from 'react';
import { firestore, collection, getDocs, doc, getDoc } from '../lib/firebase';
import { Trophy, Medal, Award } from 'lucide-react';
import { getAvatarById } from './AvatarPicker';
import { Link } from 'react-router-dom';

interface LeaderboardEntry {
  id: string;
  username: string;
  avatar_url?: string;
  wins: number;
  total_games: number;
  win_rate: number;
}

export default function Leaderboard() {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // Get all game results from Firestore
      const resultsSnap = await getDocs(collection(firestore, 'gameResults'));
      if (resultsSnap.empty) { setEntries([]); setLoading(false); return; }

      // Aggregate stats per player
      const stats = new Map<string, { wins: number; total: number }>();

      for (const d of resultsSnap.docs) {
        const r = d.data();
        for (const pid of [r.player1_id, r.player2_id]) {
          if (!stats.has(pid)) stats.set(pid, { wins: 0, total: 0 });
          stats.get(pid)!.total++;
        }
        if (r.winner_id) {
          if (!stats.has(r.winner_id)) stats.set(r.winner_id, { wins: 0, total: 0 });
          stats.get(r.winner_id)!.wins++;
        }
      }

      // Get top player IDs by wins
      const topIds = [...stats.entries()]
        .sort((a, b) => b[1].wins - a[1].wins)
        .slice(0, 20)
        .map(([id]) => id);

      if (topIds.length === 0) { setEntries([]); setLoading(false); return; }

      // Fetch profiles
      const profileMap = new Map<string, any>();
      for (const id of topIds) {
        const snap = await getDoc(doc(firestore, 'profiles', id));
        if (snap.exists()) profileMap.set(id, { id, ...snap.data() });
      }

      const leaderboard: LeaderboardEntry[] = topIds
        .map(id => {
          const s = stats.get(id)!;
          const p = profileMap.get(id);
          if (!p) return null;
          return {
            id,
            username: p.username,
            avatar_url: p.avatar_url,
            wins: s.wins,
            total_games: s.total,
            win_rate: s.total > 0 ? Math.round((s.wins / s.total) * 100) : 0,
          };
        })
        .filter(Boolean) as LeaderboardEntry[];

      setEntries(leaderboard);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-300" />;
    if (index === 2) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 text-center text-gray-300 text-sm font-mono">{index + 1}</span>;
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-yellow-400 via-pink-500 to-violet-500 text-transparent bg-clip-text">
          Leaderboard
        </h1>
        <p className="text-gray-300">Top players by wins</p>
      </div>

      {entries.length === 0 ? (
        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-8 border border-white/10 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-300">No games played yet. Be the first!</p>
          <Link
            to="/play"
            className="inline-block mt-4 px-6 py-2 bg-gradient-to-r from-pink-600 to-violet-600 rounded-lg hover:opacity-90 transition"
          >
            Play Now
          </Link>
        </div>
      ) : (
        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-4 py-3 text-sm text-gray-300 border-b border-white/10">
            <span>#</span>
            <span>Player</span>
            <span className="text-right">Wins</span>
            <span className="text-right">Games</span>
            <span className="text-right">Win %</span>
          </div>

          {entries.map((entry, index) => {
            const avatar = getAvatarById(entry.avatar_url);
            return (
              <div
                key={entry.id}
                className={`grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-4 py-3 ${
                  index < 3 ? 'bg-white/5' : ''
                } ${index !== entries.length - 1 ? 'border-b border-white/5' : ''}`}
              >
                <div className="w-5 flex justify-center">{getRankIcon(index)}</div>
                <Link to={`/player/${entry.username}`} className="flex items-center gap-3 hover:text-pink-400 transition">
                  <div className="w-8 h-8 bg-gradient-to-br from-pink-600 to-violet-600 rounded-full flex items-center justify-center shrink-0">
                    {avatar ? <span className="text-base">{avatar.emoji}</span> : <span className="text-sm font-bold">{entry.username.charAt(0)}</span>}
                  </div>
                  <span className="font-medium truncate">{entry.username}</span>
                </Link>
                <span className="text-right font-semibold text-green-400">{entry.wins}</span>
                <span className="text-right text-gray-300">{entry.total_games}</span>
                <span className="text-right text-pink-400">{entry.win_rate}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
